import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check } from 'lucide-react';
import { submitGuess } from '../lib/guesses';

const MAX_GUESSES = 3; // per browser, per session

// "Guess who sent this 🤔" — small button + pop-up for recipients. No account
// needed. The recipient is never told whether they're right.
export default function GuessWho({ testId }: { testId: string }) {
  const key = `yhuu_guesses_${testId}`;
  const used = () => { try { return Number(localStorage.getItem(key) || '0'); } catch { return 0; } };

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'invalid' | 'error' | 'limit'>('idle');

  const close = () => {
    setOpen(false);
    if (status === 'done' || status === 'limit') { setName(''); setStatus('idle'); }
  };

  const send = async () => {
    const guess = name.trim();
    if (!guess || status === 'sending') return;
    if (used() >= MAX_GUESSES) { setStatus('limit'); return; }
    setStatus('sending');
    const res = await submitGuess(testId, guess);
    if (res.ok) {
      try { localStorage.setItem(key, String(used() + 1)); } catch { /* ignore */ }
      setStatus('done');
    } else {
      setStatus(res.error === 'invalid' ? 'invalid' : 'error');
    }
  };

  return (
    <>
      <button
        onClick={() => { if (used() >= MAX_GUESSES) setStatus('limit'); setOpen(true); }}
        data-track="guess_open"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 14, fontWeight: 900, color: '#be123c',
          background: '#fff0f4', border: '1.5px solid #fbcfe0',
        }}
      >
        🤔 Guess who sent this before replying?
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          >
            <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} onClick={close} />

            <motion.div
              className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
              style={{ maxHeight: '92vh', overflowY: 'auto', background: '#fff' }}
              initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 340, damping: 30 }}
            >
              <div className="relative text-center" style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)', padding: '22px 56px' }}>
                <p style={{ color: '#fff', fontWeight: 900, fontSize: 20, letterSpacing: -0.3 }}>Who sent this?</p>
                <button
                  onClick={close} aria-label="Close"
                  style={{ position: 'absolute', top: '50%', right: 14, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <X size={16} color="#fff" />
                </button>
              </div>

              {status === 'done' || status === 'limit' ? (
                <div style={{ padding: '32px 24px 26px', textAlign: 'center', color: '#18181b' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff0f4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 30 }}>
                    {status === 'done' ? '🤫' : '🙈'}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: -0.5 }}>
                    {status === 'done' ? 'Guess sent' : 'That’s enough guesses'}
                  </div>
                  <p style={{ fontSize: 15, lineHeight: 1.5, color: '#52525b', fontWeight: 600, margin: '10px 0 22px' }}>
                    {status === 'done'
                      ? 'They’ll see that you guessed. We never say if you’re right.'
                      : 'You’ve used your guesses for this one.'}
                  </p>
                  <button
                    onClick={close}
                    style={{ width: '100%', padding: 17, borderRadius: 999, border: 'none', cursor: 'pointer', background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff', fontWeight: 900, fontSize: 16 }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div style={{ padding: '24px 22px 24px', color: '#18181b', textAlign: 'left' }}>
                  <p style={{ fontSize: 15, lineHeight: 1.5, color: '#52525b', fontWeight: 600, marginBottom: 14 }}>
                    Who do you think sent this? Only the sender sees your guess, and we never tell you if you’re right.
                  </p>
                  <input
                    value={name}
                    onChange={e => { setName(e.target.value); if (status !== 'idle') setStatus('idle'); }}
                    onKeyDown={e => e.key === 'Enter' && send()}
                    maxLength={40}
                    placeholder="Type a name"
                    autoFocus
                    style={{ width: '100%', boxSizing: 'border-box', padding: '15px 16px', borderRadius: 14, fontSize: 16, border: '1.5px solid #f3d3de', background: '#fff7fa', color: '#18181b', outline: 'none', fontFamily: 'inherit', marginBottom: 14 }}
                  />
                  {status === 'invalid' && (
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#be123c', marginBottom: 12 }}>
                      Just a name please, no links, numbers or handles.
                    </p>
                  )}
                  {status === 'error' && (
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#be123c', marginBottom: 12 }}>
                      Couldn’t send your guess. Please check your connection and try again.
                    </p>
                  )}
                  <button
                    onClick={send} disabled={!name.trim() || status === 'sending'}
                    style={{
                      width: '100%', padding: 18, borderRadius: 999, border: 'none', fontFamily: 'inherit', fontWeight: 900, fontSize: 16, color: '#fff',
                      background: 'linear-gradient(90deg, #e11d48, #ec4899)', cursor: name.trim() ? 'pointer' : 'not-allowed',
                      opacity: !name.trim() || status === 'sending' ? 0.5 : 1, boxShadow: '0 10px 24px rgba(225,29,72,0.28)',
                    }}
                  >
                    {status === 'sending' ? 'Sending…' : 'Send my guess'}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
